import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';

/**
 * Shown by ProtectedRoute when a user with a verified TOTP factor is still at
 * aal1 — they must enter a code to elevate to aal2 before reaching the app.
 */
export function TwoFactorChallenge({ onVerified }: { onVerified: () => void }) {
  const { signOut } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    setBusy(true);
    try {
      const { data: factors, error: lerr } = await supabase.auth.mfa.listFactors();
      if (lerr) throw lerr;
      const totp = (factors?.all ?? []).find((f) => f.factor_type === 'totp' && f.status === 'verified');
      if (!totp) throw new Error('No 2FA factor found');

      const ch = await supabase.auth.mfa.challenge({ factorId: totp.id });
      if (ch.error) throw ch.error;
      const res = await supabase.auth.mfa.verify({ factorId: totp.id, challengeId: ch.data.id, code: code.trim() });
      if (res.error) throw res.error;
      onVerified();
    } catch (err: any) {
      toast({ title: 'Invalid code', description: 'That code was incorrect or expired. Try the latest one.', variant: 'destructive' });
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Card className="glass-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">Two-factor verification</CardTitle>
            <CardDescription>Enter the 6-digit code from your authenticator app</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <Input
                autoFocus
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center font-mono text-lg tracking-[0.4em]"
              />
              <Button type="submit" className="w-full" disabled={busy || code.length < 6}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify
              </Button>
            </form>
            <button type="button" onClick={() => signOut()} className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground">
              Sign out
            </button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
